import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PPUIGrid } from 'pricing-pages-ui-components/grid/PPUIGrid';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import { STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD } from '../../constants/tracking/TrackingSources';
import { SuiteCTA } from '../../features/purchaseMotions/components/SuiteCTA';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import useSuitePurchaseMotions from '../../features/purchaseMotions/hooks/useSuitePurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAppSelector } from '../../reducers';
import { isAppAuthSelector } from '../../selectors/AuthSelectors';
import { productSelector } from '../../selectors/ProductsSelectors';
import { useFTS0011ExperimentAndLogExposure } from '../../utils/experiments/fts0011';
import { logFTS0012Exposure, useFTS0012Experiment } from '../../utils/experiments/fts0012';
import { trackClickedLink } from '../../utils/tracking/trackingEventUtils';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { SuiteBadge } from '../common/badges/SuiteBadge';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import AdditionalUnitsPickerCompactAll from '../common/quantityPacks/AdditionalUnitsPickerCompactAll';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import { MerchCardTitleContainer, MerchCardWrapper } from './MerchCardSharedComponents';
import StarterCustomerPlatformUpsellCardBusinessObjectives from './StarterCustomerPlatformUpsellCardBusinessObjectives';
import SuiteTermSelectorContainer from './SuiteTermSelectorContainer';
import BusinessObjectivesContainer from './business-objectives/BusinessObjectivesContainer';
import CrmSuitePriceMerchCard from './price-display/CrmSuitePriceMerchCard';
import { useSkuAdditionalUnits } from './useSkuAdditionalUnits';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StarterCustomerPlatformUpsellCard = ({
  productLine,
  screen,
  selectedTab,
  gridItemProps
}) => {
  const productLevel = STARTER;
  const history = useHistory();
  const isAppReady = useGlobalLoading();
  const {
    isFTS0012Variant,
    isEligibleForFTS0012
  } = useFTS0012Experiment();
  if (isEligibleForFTS0012) {
    logFTS0012Exposure();
  }
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product === null || product === void 0 ? void 0 : product.apiName;
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const handleLearnMoreClick = () => {
    trackClickedLink({
      name: 'learn-more-link',
      type: 'internal',
      url: '/suite',
      trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
      value: getI18nText('merchCards.starterCustomerPlatformUpsellCard.cta', {
        locale: 'en-us'
      })
    });
    history.push(selectedTab ? `/suite/${selectedTab}` : '/suite');
  };
  const {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const additionalUnitsPickerProps = {
    additionalUnitSkuId,
    additionalUnitQuantity,
    setAdditionalUnitQuantity,
    productConfigurations: [createProductConfig({
      skuId: skuId || 0,
      quantity: 1
    })],
    queryParamTerm,
    trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
    productLevel
  };
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const purchaseMotions = useSuitePurchaseMotions({
    productLevel,
    returnFirstCTAOnly: isAppAuth
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  const {
    isFTS0011Variant
  } = useFTS0011ExperimentAndLogExposure();
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsxs(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 1,
      paddingBlockStart: "800",
      position: "relative",
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      children: [/*#__PURE__*/_jsx(SuiteBadge, {
        productLevel: productLevel
      }), /*#__PURE__*/_jsx(MerchCardTitleContainer, {
        children: /*#__PURE__*/_jsx(SectionTitle, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "merchCards.suiteTile.starterCard.title"
          })
        })
      })]
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 2,
      paddingBlockStart: "600",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "merchCards.suiteTile.starterCard.description"
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 3,
      paddingBlockStart: "400",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        children: /*#__PURE__*/_jsx(CrmSuitePriceMerchCard, {
          productLevel: productLevel
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 4,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [41],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(SuiteTermSelectorContainer, {
          productLevel: productLevel,
          onChange: setQueryParamTerm,
          queryParamTerm: queryParamTerm,
          trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 5,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [34, 76],
        widths: ['75%', '100%'],
        children: /*#__PURE__*/_jsx(AdditionalUnitsPickerCompactAll, Object.assign({}, additionalUnitsPickerProps))
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      marginBlockEnd: "500",
      gridRowStart: 6,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [47],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            skuId: skuId,
            screen: screen,
            trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD
          }),
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            justifyContent: "center",
            gap: "200",
            inlineSize: "100%",
            children: [purchaseMotions.map(purchaseMotion => /*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
                purchaseMotion: purchaseMotion,
                productLevel: productLevel,
                trackingSource: STARTER_CUSTOMER_PLATFORM_UPSELL_MERCH_CARD,
                screen: screen,
                purchaseMotionLocation: 'MERCH_CARD',
                use: purchaseMotion === BUY_TOUCHLESS && isFTS0011Variant ? 'secondary' : undefined
              }, ctaVariantProps))
            }, purchaseMotion)), /*#__PURE__*/_jsx(Button, Object.assign({
              use: "transparent",
              onClick: handleLearnMoreClick,
              "aria-label": I18n.text('merchCards.starterCustomerPlatformUpsellCard.cta', {
                product: I18n.text('merchCards.suiteTile.starterCard.title')
              })
            }, ctaVariantProps, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "merchCards.starterCustomerPlatformUpsellCard.cta"
              })
            })), isFTS0011Variant && /*#__PURE__*/_jsx(Text, {
              typography: "detail-100",
              textAlign: "center",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "merchCards.starterCustomerPlatformUpsellCard.noCreditCardRequired"
              })
            })]
          })
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGrid, Object.assign({}, gridItemProps, {
      gridTemplateRows: "subgrid",
      gridRowStart: 7,
      gridRowEnd: 9,
      marginBlockEnd: "700",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [47, 47],
        widths: ['100%', '100%'],
        children: isFTS0012Variant && apiName ? /*#__PURE__*/_jsx(BusinessObjectivesContainer, {
          productLine: productLine,
          productLevel: productLevel,
          apiName: apiName
        }) : /*#__PURE__*/_jsx(StarterCustomerPlatformUpsellCardBusinessObjectives, {
          currentProductLine: productLine
        })
      })
    }))]
  });
};
export default StarterCustomerPlatformUpsellCard;